/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Image } from 'lucide-react';
import { useUploadTemplate } from '@/hooks/api/useTemplateCatalog';
import { useNotification } from '@/hooks/useNotification';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nextSortOrder?: number;
}

export default function UploadTemplateModal({ isOpen, onClose, nextSortOrder = 1 }: Props) {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();
  const uploadMutation = useUploadTemplate();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [sortOrder, setSortOrder] = useState(nextSortOrder);
  const [jrxmlFile, setJrxmlFile] = useState<File | null>(null);
  const [letterheadFile, setLetterheadFile] = useState<File | null>(null);

  const jrxmlInputRef = useRef<HTMLInputElement>(null);
  const letterheadInputRef = useRef<HTMLInputElement>(null);

  // Sync sortOrder when nextSortOrder prop changes (e.g. after templates load)
  useEffect(() => {
    setSortOrder(nextSortOrder);
  }, [nextSortOrder]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!jrxmlFile) {
      showError(t('templateCatalog.upload.jrxmlRequired'));
      return;
    }

    const formData = new FormData();
    const metadata = JSON.stringify({ code, name, description, isPremium, sortOrder });
    formData.append('metadata', new Blob([metadata], { type: 'application/json' }));
    formData.append('jrxml', jrxmlFile);
    if (letterheadFile) {
      formData.append('letterhead', letterheadFile);
    }

    try {
      await uploadMutation.mutateAsync(formData);
      success(t('templateCatalog.upload.success'));
      resetForm();
      onClose();
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.upload.error'));
    }
  };

  const resetForm = () => {
    setCode('');
    setName('');
    setDescription('');
    setIsPremium(false);
    setSortOrder(nextSortOrder);
    setJrxmlFile(null);
    setLetterheadFile(null);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t('templateCatalog.upload.title')}</h2>
          <button onClick={onClose} className="modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>{t('templateCatalog.fields.code')}</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="e.g. my-template"
              required
              pattern="^[a-z0-9-]{3,50}$"
              className="form-input"
            />
            <span className="form-hint">{t('templateCatalog.fields.codeHint')}</span>
          </div>

          <div className="form-group">
            <label>{t('templateCatalog.fields.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Custom Template"
              required
              maxLength={100}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{t('templateCatalog.fields.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('templateCatalog.fields.descriptionPlaceholder')}
              rows={2}
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('templateCatalog.fields.sortOrder')}</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                min={0}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPremium}
                  onChange={(e) => setIsPremium(e.target.checked)}
                />
                {t('templateCatalog.fields.premium')}
              </label>
            </div>
          </div>

          {/* JRXML File */}
          <div className="form-group">
            <label>{t('templateCatalog.fields.jrxmlFile')}</label>
            <div
              className="dropzone"
              onClick={() => jrxmlInputRef.current?.click()}
            >
              <FileText size={24} />
              {jrxmlFile ? (
                <span className="dropzone-filename">{jrxmlFile.name}</span>
              ) : (
                <span>{t('templateCatalog.upload.selectJrxml')}</span>
              )}
            </div>
            <input
              ref={jrxmlInputRef}
              type="file"
              accept=".jrxml"
              onChange={(e) => setJrxmlFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
          </div>

          {/* Letterhead File */}
          <div className="form-group">
            <label>{t('templateCatalog.fields.letterhead')} ({t('templateCatalog.fields.optional')})</label>
            <div
              className="dropzone"
              onClick={() => letterheadInputRef.current?.click()}
            >
              <Image size={24} />
              {letterheadFile ? (
                <span className="dropzone-filename">{letterheadFile.name}</span>
              ) : (
                <span>{t('templateCatalog.upload.selectLetterhead')}</span>
              )}
            </div>
            <input
              ref={letterheadInputRef}
              type="file"
              accept=".png"
              onChange={(e) => setLetterheadFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={uploadMutation.isPending}
            >
              <Upload size={14} />
              {uploadMutation.isPending ? t('common.uploading') : t('templateCatalog.upload.submit')}
            </button>
          </div>
        </form>
      </div>
      <style>{modalStyles}</style>
    </>
  );
}

const modalStyles = `
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 1000;
  }
  .modal-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 520px;
    max-height: 90vh;
    overflow-y: auto;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    z-index: 1001;
  }
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .modal-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }
  .modal-close {
    background: none;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.15s;
  }
  .modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
  .modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 12px;
    border-top: 1px solid var(--color-border-subtle);
  }
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .form-group label {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }
  .form-input {
    padding: 8px 12px;
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-size: 13px;
    outline: none;
    transition: border-color 0.15s;
  }
  .form-input:focus {
    border-color: var(--color-brand);
  }
  textarea.form-input {
    resize: vertical;
    min-height: 60px;
  }
  .form-hint {
    font-size: 11px;
    color: var(--color-text-muted);
  }
  .form-row {
    display: flex;
    gap: 16px;
    align-items: flex-end;
  }
  .form-row .form-group {
    flex: 1;
  }
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px !important;
    padding-bottom: 8px;
  }
  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--color-brand);
  }
  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px;
    border: 2px dashed var(--color-border-default);
    border-radius: 8px;
    cursor: pointer;
    color: var(--color-text-muted);
    font-size: 12px;
    transition: all 0.15s;
  }
  .dropzone:hover {
    border-color: var(--color-brand);
    color: var(--color-text-secondary);
  }
  .dropzone-filename {
    color: var(--color-text-primary);
    font-weight: 500;
  }
  .btn-primary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: var(--color-brand);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
  }
  .btn-primary:hover { opacity: 0.9; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary {
    padding: 8px 16px;
    background: transparent;
    color: var(--color-text-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }
`;
